import { hueFor, initials } from './avatar';
import type { ContactCardData } from './types';

/**
 * ONE card holding ALL matched contacts, laid out as a single-column LIST — each
 * contact on its own row (avatar + name + email). Every match renders (a contact
 * with no email still shows, falling back to title/company). A matched row is
 * clickable → onOpenContact(id), which opens the existing ContactCardPopover.
 * An UNRESOLVED invitee (no id — e.g. an event attendee with no saved contact)
 * renders as an identity row with an "Add contact" affordance → onAddContact.
 */

export function ContactsGridCard({
  data,
  onOpenContact,
  onAddContact,
}: {
  data: ContactCardData;
  onOpenContact: (id: string) => void;
  /** Invoked for an UNRESOLVED invitee cell (no saved contact, no id). */
  onAddContact?: (contact: { name: string; email?: string }) => void;
}) {
  const contacts = data.contacts ?? [];
  if (!contacts.length) return null;

  // For a FILTERED group (role/company/title search) show that label as the
  // header so the card reads e.g. "👤 SOCCER PLAYERS · 6"; otherwise the generic
  // "CONTACTS". Trimmed + capped so an odd query can't blow out the header.
  const label = data.label?.trim();
  const kind = label ? label.slice(0, 60).toUpperCase() : 'CONTACTS';

  return (
    <div className="rc-card rc-contacts">
      <div className="rc-meta">
        <span className="rc-meta-kind">👤 {kind}</span>
        <span className="rc-meta-sep">·</span>
        <span className="rc-meta-match">{contacts.length}</span>
      </div>

      <div className="rc-contacts-grid">
        {contacts.map((c, i) => {
          // Prefer the email (the common "give me their emails" ask); fall back
          // to title/company so a contact with no email still shows a subtitle —
          // and always renders (never filtered out).
          const sub = c.email || c.title || c.company || '';
          // No id → unresolved invitee: clicking offers to create the contact.
          const isUnresolved = !c.id;
          const onClick = isUnresolved
            ? () => onAddContact?.({ name: c.name, email: c.email })
            : () => onOpenContact(c.id!);
          return (
            <button
              key={c.id ?? c.email ?? `${c.name}-${i}`}
              type="button"
              className={`rc-contact-cell${isUnresolved ? ' rc-contact-cell-unresolved' : ''}`}
              onClick={onClick}
              title={isUnresolved ? 'Not in your contacts — add them' : undefined}
            >
              {c.photoUrl ? (
                <img className="rc-av" src={c.photoUrl} alt="" />
              ) : (
                <div
                  className="rc-av rc-av-initials"
                  style={{ background: hueFor(c.email || c.name || String(i)) }}
                >
                  {initials(c.name, c.email || '')}
                </div>
              )}
              <div className="rc-contact-text">
                <span className="rc-contact-name">{c.name || c.email || 'Unknown'}</span>
                {sub && <span className="rc-contact-sub">{sub}</span>}
              </div>
              {isUnresolved && <span className="rc-contact-add" aria-hidden="true">+ Add</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
